/**
 * 设备检测工具
 */

/**
 * 检测是否为浏览器环境
 */
export function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined'
}

/**
 * 检测是否为服务端环境
 */
export function isServer(): boolean {
  return !isBrowser()
}

/**
 * 检测是否为移动设备
 */
export function isMobile(): boolean {
  if (!isBrowser()) return false

  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
}

/**
 * 检测是否为微信环境
 */
export function isWeChat(): boolean {
  if (!isBrowser()) return false

  return /MicroMessenger/i.test(navigator.userAgent)
}

/**
 * 检测是否为微信小程序环境
 */
export function isMiniProgram(): boolean {
  if (!isBrowser()) return false

  return (
    /miniProgram/i.test(navigator.userAgent) || (window as any).__wxjs_environment === 'miniprogram'
  )
}

/**
 * 获取设备环境（兼容旧版 v2 逻辑）
 * 返回值：app | mp-weixin | pc-wechat | phone-wechat | desktop-app | pc-web | phone-web | ''
 */
export function getDeviceEnvironment(): string {
  if (!isBrowser()) return ''

  // 兼容小程序/APP（在 H5 里尽量识别）
  if ((window as any).__wxjs_environment === 'miniprogram') {
    return 'mp-weixin'
  }
  if (typeof (window as any).plus !== 'undefined') {
    return 'app'
  }

  const ua = navigator.userAgent
  const lowerUA = ua.toLowerCase()
  const isPc = !/Android|webOS|iPhone|iPad|iPod|BlackBerry|Mobile|IEMobile|Opera Mini/i.test(ua)

  if (lowerUA.includes('micromessenger') && !lowerUA.includes('wxwork')) {
    return isPc ? 'pc-wechat' : 'phone-wechat'
  }

  if ((window as any).win_message !== undefined || ua.includes('Electron')) {
    return 'desktop-app'
  }

  return isPc ? 'pc-web' : 'phone-web'
}

/**
 * 兼容旧版的设备 ID 计算逻辑
 */
export function getDeviceId(): string {
  if (!isBrowser()) return 'server'

  const device = getDeviceEnvironment()
  if (device === 'mp-weixin' || device === 'app') {
    return device
  }
  if (device === 'pc-wechat' || device === 'phone-wechat') {
    // 微信 OAuth 旧接口按 applet 识别 H5 微信环境，PC 微信不能再被桌面 UA 覆盖为 Window/Mac。
    return 'applet'
  }

  let deviceId = new Date().getTime().toString().slice(-8) + 'pc'
  const uA = navigator.userAgent.toLowerCase()

  if (uA.includes('micromessenger') && !uA.includes('wxwork')) {
    deviceId = 'applet'
  }

  const ipad = /ipad/i.test(uA)
  const iphone = /iphone os/i.test(uA)
  const midp = /midp/i.test(uA)
  const uc7 = /rv:1.2.3.4/i.test(uA)
  const uc = /ucweb/i.test(uA)
  const android = /android/i.test(uA)
  const windowsce = /windows ce/i.test(uA)
  const windowsmd = /windows mobile/i.test(uA)

  if (!(ipad || iphone || midp || uc7 || uc || android || windowsce || windowsmd)) {
    const agent = navigator.userAgent.toLowerCase()
    const isMac = /macintosh|mac os x/i.test(navigator.userAgent)

    if (agent.indexOf('win32') >= 0 || agent.indexOf('wow32') >= 0) {
      return 'Window32'
    }
    if (agent.indexOf('win64') >= 0 || agent.indexOf('wow64') >= 0) {
      return 'Window64'
    }
    if (isMac) {
      return 'Mac'
    }
    if (/android|adr/gi.test(uA)) {
      return 'Android_Phone'
    } else if (/\(i[^;]+;( U;)? CPU.+Mac OS X/gi.test(uA)) {
      return 'IOS_Phone'
    } else if (/iPad/gi.test(uA)) {
      return 'ipad'
    }
  }

  return deviceId
}

/**
 * 检测是否为 App WebView 环境
 * 通过 userAgent 检测 uni-app 标识
 */
export function isAppWebView(): boolean {
  if (!isBrowser()) return false

  // 检测 uni-app webview 环境（通过 userAgent）
  if (navigator.userAgent.indexOf('uni-app') !== -1) {
    return true
  }

  return false
}

/**
 * 是否为小程序内置 WebView（旧版逻辑）
 */
export function isMiniWechatWebView(): boolean {
  if (!isBrowser()) return false

  const lowerUA = navigator.userAgent.toLowerCase()
  if (lowerUA.includes('miniprogram') || (window as any).__wxjs_environment === 'miniprogram') {
    return true
  }

  let result = false
  const wx = (window as any).wx
  if (wx?.miniProgram?.getEnv) {
    wx.miniProgram.getEnv((res: { miniprogram?: boolean }) => {
      result = !!res.miniprogram
    })
  }

  return result
}

/**
 * 获取设备信息
 */
export function getDeviceInfo() {
  return {
    isBrowser: isBrowser(),
    isServer: isServer(),
    isMobile: isMobile(),
    isWeChat: isWeChat(),
    isMiniProgram: isMiniProgram(),
    userAgent: isBrowser() ? navigator.userAgent : ''
  }
}

/**
 * 检测是否为桌面应用环境
 * 兼容旧版 isDesktop 函数，返回数字：
 * - 3: 桌面应用
 * - 其他: 非桌面应用
 */
export function isDesktop(): number {
  return getDeviceEnvironment() === 'desktop-app' ? 3 : 0
}
